/**
 * Component configuration page — Phase 5 replacement for the Phase 4
 * stub.
 *
 * Given a `componentId` in the URL, renders:
 *
 * 1. **Live preview** — the registered component rendered with its
 *    last-captured runtime props, or with the registry's optional
 *    `previewProps` as a fallback. Wrapped in a local
 *    {@link PreviewErrorBoundary} so a bad render does not crash the
 *    whole page.
 *
 * 2. **Prop labels** — a row of editable pills, one per prop. Clicking a
 *    pill replaces it with an inline `TextField` + save/cancel buttons
 *    that upsert against `/claimflow/component-prop-labels`.
 *
 * 3. **Existing rules** — a `Table` of every rule bound to this
 *    component, with status-aware row actions (activate, archive,
 *    delete) that target the corresponding service endpoints.
 *
 * 4. **Add rule form** — an `EntityForm` with `name`, `description`,
 *    `nlInput` fields. Submit does not create the rule directly; it
 *    calls the `translate` endpoint and opens a preview `Modal`. The
 *    admin reviews the parsed conditions / effects and then confirms,
 *    which creates the rule as a draft.
 *
 * Every UI element comes from `.claude-work/context/component-catalog.md`
 * — Cards, Badges, Tables, Modals, Alerts, TextFields, Buttons,
 * DropdownMenus, etc. No ad-hoc markup.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type UIEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, Code2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { Button, DropdownMenu } from '@components/base/actions';
import { Table } from '@components/base/data';
import { Badge, Icon, Text, Tooltip } from '@components/base/display';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { RadioGroup, Select, TextField } from '@components/base/inputs';
import { Card, HStack, Panel, VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import {
  EntityForm,
  useEntityForm,
  type EntityFormConfig,
} from '@components/integrated/entity-form';

import {
  applyRules,
  getComponent,
  getLayoutConfig,
  getRuntimeContext,
  getRuntimeProps,
  ruleJsonSchema,
  validateEffectsAgainstRegistration,
  type ComponentRegistration,
  type ComponentRule,
  type PublishedLayoutConfig,
  type RuleEffect,
  type RuleStatus,
  type RuntimeContextCapture,
} from '@components/shared/configurable';

import type {
  ComponentPropLabelDTO,
  ComponentRuleDTO,
  TranslateComponentRuleResponse,
  UpdateComponentRulePayload,
} from '../../api/componentRulesService';
import {
  useComponentPropLabels,
  useComponentRulesList,
  useComponentRulesAuditLog,
  useCreateComponentRule,
  useDeleteComponentRule,
  useTranslateComponentRule,
  useUpdateComponentRule,
  useUpdateComponentRuleStatus,
  useUpdateComponentRuleVisibility,
  useUpsertComponentPropLabel,
} from '../../hooks/componentRules';

import { tenantsService } from '../../api/tenantsService';
import { useAdminAuthStore } from '../../auth/store';
import { useIsAdminTenant } from '../../auth/useIsAdminTenant';

import { LiveContextRow } from './LiveContextRow';
import { PreviewErrorBoundary } from './PreviewErrorBoundary';
import {
  getPropJsonSchemasFromRegistration,
  getPropNamesFromRegistration,
} from './propIntrospection';
import { LayoutAuthoringPanel } from './LayoutAuthoringPanel';
import { discoverLayoutTargets } from './discoverLayoutTargets';
import { discoverConfigTargets } from './discoverConfigTargets';
import { discoverRowTargets } from './discoverRowTargets';
import {
  applyEdit,
  buildDraftRule,
  buildLayoutRuleJson,
  type LayoutEdit,
  type LayoutTarget,
} from './layoutAuthoring';

// ---------------------------------------------------------------------------
// Route params
// ---------------------------------------------------------------------------

/**
 * Route params for `/admin/components/:componentId/config`.
 *
 * Indexed with `string` because `useParams` from react-router requires a
 * `Record<string, string | undefined>`-compatible constraint.
 */
type ComponentConfigRouteParams = Record<string, string | undefined> & {
  componentId?: string;
};

// ---------------------------------------------------------------------------
// Origin tenant (cross-module read of the live app's active tenant)
// ---------------------------------------------------------------------------

/**
 * Read the live ClaimFlow app's currently-active tenant id from
 * `localStorage['claimflow_active_tenant_id']`.
 *
 * This is a **deliberate cross-module read**: the admin config page is reached
 * from the live ClaimFlow module (e.g. via CMD+click from a page rendering the
 * component), so the tenant the admin was viewing there is the natural default
 * for both the save-target and the "preview as" selectors. The stored value is
 * a stringified numeric tenant id in the SAME id space as the admin
 * `tenantsService`, so it lines up with a `tenantOptions` value once that query
 * loads.
 *
 * Null-safe: returns `null` when nothing is stored (the seeds then fall back to
 * the admin auth store's `activeTenantId`).
 */
function getOriginTenantId(): string | null {
  return localStorage.getItem('claimflow_active_tenant_id');
}

// ---------------------------------------------------------------------------
// Status → badge variant mapping
// ---------------------------------------------------------------------------

/**
 * Translate a `RuleStatus` into the badge variant the design system
 * offers. Kept as a pure function so the mapping can be reused by the
 * row-action menu below the Table.
 */
function statusBadgeVariant(
  status: RuleStatus
): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' {
  switch (status) {
    case 'active':
      return 'success';
    case 'archived':
      return 'default';
    case 'draft':
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// Live-preview active-rule sourcing
// ---------------------------------------------------------------------------

/**
 * Convert a snake-cased {@link ComponentRuleDTO} into the camelCased
 * {@link ComponentRule} the evaluator (`applyRules`) consumes.
 *
 * Mirrors the runtime DTO → domain mapper in
 * `@components/shared/configurable/useComponentRules` (`mapRuleFromDto`) so the
 * preview applies active rules through exactly the same shape the
 * `withComponentRules` HOC does at runtime. Kept local (not imported from the
 * runtime hook) because that hook's mapper is private to its module.
 */
function mapActiveRuleFromDto(dto: ComponentRuleDTO): ComponentRule {
  return {
    id: dto.id,
    componentId: dto.component_id,
    name: dto.name,
    description: dto.description ?? '',
    nlInput: dto.nl_input ?? '',
    conditions: dto.rule_json.conditions,
    effects: dto.rule_json.effects,
    status: dto.status,
    priority: dto.priority,
  };
}

/**
 * Select the active rules that WOULD apply to the previewed tenant, mapped to
 * the evaluator shape and sorted by `priority` ascending (ties left in input
 * order; `applyRules` itself re-sorts and tie-breaks by `id`, so this ordering
 * only needs to be priority-stable).
 *
 * A rule applies when it is `active` AND either
 *  - `visibility === 'public'` (shared across all tenants — `tenant_id` is
 *    `null`, caught by this arm), or
 *  - it is `private` and its `tenant_id` matches `previewTenantId` (compared as
 *    strings so a numeric/string id mismatch never silently drops a match).
 *
 * When `previewTenantId` is `null` only public rules can apply (a private rule
 * has nothing to match against).
 */
function selectActiveRulesForTenant(
  dtos: readonly ComponentRuleDTO[],
  previewTenantId: string | null
): ComponentRule[] {
  const applicable = dtos.filter((dto) => {
    if (dto.status !== 'active') return false;
    if (dto.visibility === 'public') return true;
    if (previewTenantId === null) return false;
    // Coerce `tenant_id` (typed `string | null`, but a backend could send a
    // numeric id) so a numeric/string id mismatch never silently drops a
    // private rule. `previewTenantId` is already narrowed to `string` here.
    return String(dto.tenant_id) === previewTenantId;
  });
  // Stable priority-ascending sort. `applyRules` re-sorts internally, but
  // sorting here keeps the rule list passed to it (and assertable in tests)
  // in the documented evaluation order.
  const sorted = [...applicable].sort((a, b) => a.priority - b.priority);
  return sorted.map(mapActiveRuleFromDto);
}

// ---------------------------------------------------------------------------
// Add-rule form config (EntityForm)
// ---------------------------------------------------------------------------

/**
 * Build the `EntityFormConfig` for the add-rule form.
 *
 * The config is declared at render time because it doesn't depend on
 * any runtime state. We supply a noop `erpEndpoint` — the form never
 * submits to that URL, because the page passes a custom `mutationFn`
 * that calls the translate service instead.
 */
const addRuleFormConfig: EntityFormConfig = {
  id: 'component-rules-add-rule',
  endpoint: {
    // Unused at runtime; EntityForm requires a value even when the
    // caller provides a custom mutationFn.
    erpEndpoint: '/claimflow/component-rules/translate',
    method: 'POST',
  },
  fields: {
    name: {
      type: 'text',
      label: 'Name',
      placeholder: 'e.g. Hide coverage card for Sinistre',
      order: 1,
      validation: {
        required: true,
        max: { value: 200, message: 'Name must be 200 characters or fewer' },
      },
    },
    description: {
      type: 'textarea',
      label: 'Description',
      placeholder: 'Optional notes about why this rule exists',
      order: 2,
      options: { rows: 2, maxLength: 2000, resize: 'vertical' },
    },
    // NOTE: `nlInput` is intentionally NOT an EntityForm field. It's rendered
    // below by the local `NLInputWithHighlights` component so we can wrap an
    // `aria-hidden` backdrop around the textarea to highlight `{{token}}`
    // occurrences. EntityForm.setValue('nlInput', ...) still stores the
    // value in the form's Zustand store, so the submit payload includes it.
  },
  actions: {
    // Hidden — the page renders a custom submit button so it can be gated on
    // the (non-EntityForm-field) `nlInput` being non-empty. The form's native
    // `<form onSubmit>` still fires, so `<Button type="submit">` is enough.
    submit: { visible: false },
    cancel: { visible: false },
    reset: { visible: false },
  },
  layout: { columns: 1 },
};

// ---------------------------------------------------------------------------
// NL input with `{{token}}` highlighting
// ---------------------------------------------------------------------------

/**
 * Regex capturing every `{{...}}` token in the NL rule input. No whitespace
 * is allowed between the braces to keep highlighting in lockstep with what
 * the translator treats as an identifier.
 */
const TOKEN_REGEX = /\{\{[^{}\s]+\}\}/g;

interface NLInputWithHighlightsProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly maxLength?: number;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly ariaLabel?: string;
}

/**
 * A textarea with a transparent-text backdrop that renders the same
 * value but wraps `{{token}}` substrings in `<mark>` for highlighting.
 * The textarea's `onScroll` syncs the backdrop's scroll position so the
 * highlights stay glued to their tokens when the input overflows.
 *
 * The ref forwarded to consumers is the underlying `<textarea>` element,
 * so the page can read `selectionStart` / `selectionEnd` and call
 * `setSelectionRange` after inserting a token programmatically.
 */
const NLInputWithHighlights = forwardRef<HTMLTextAreaElement, NLInputWithHighlightsProps>(
  function NLInputWithHighlights(
    { value, onChange, maxLength, placeholder, disabled, id, ariaLabel },
    forwardedRef
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const backdropRef = useRef<HTMLDivElement | null>(null);

    // Expose the textarea element (not a wrapper) so consumers can manipulate
    // selection and focus directly.
    useImperativeHandle(forwardedRef, () => textareaRef.current as HTMLTextAreaElement);

    const handleScroll = (event: UIEvent<HTMLTextAreaElement>) => {
      const backdrop = backdropRef.current;
      if (backdrop === null) return;
      backdrop.scrollTop = event.currentTarget.scrollTop;
      backdrop.scrollLeft = event.currentTarget.scrollLeft;
    };

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    };

    // Shared typography + padding + border classes so the backdrop wraps
    // identically to the real textarea. Any change here must be kept in sync
    // across both nodes.
    const sharedTypography =
      'font-sans text-sm leading-6 px-3 py-2 border border-gray-300 rounded-md';

    return (
      <div className="relative w-full">
        <div
          ref={backdropRef}
          aria-hidden="true"
          className={`absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words text-transparent ${sharedTypography}`}
        >
          {renderHighlightedFragments(value)}
        </div>
        <textarea
          ref={textareaRef}
          id={id}
          aria-label={ariaLabel}
          value={value}
          onChange={handleChange}
          onScroll={handleScroll}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          rows={4}
          className={`relative w-full bg-transparent resize-y focus:outline-none focus:border-primary-400 ${sharedTypography}`}
        />
      </div>
    );
  }
);

/**
 * Split `value` on `{{token}}` substrings, returning an array of strings
 * and `<mark>` elements suitable for rendering inside the backdrop div.
 *
 * The tone (`bg-info-50 text-info-800`) matches the info `Badge` variant so
 * the highlight colour stays consistent with the Prop-label and
 * Context-variable pills the admin clicks to insert tokens in the first
 * place.
 *
 * Trailing-newline caveat: CSS `white-space: pre-wrap` collapses the final
 * newline visually unless followed by *something*. We append a `<br />`
 * sentinel when the value ends with `'\n'` so the backdrop's final empty
 * line matches the textarea's.
 */
function renderHighlightedFragments(value: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(TOKEN_REGEX.source, 'g');

  while ((match = regex.exec(value)) !== null) {
    if (match.index > cursor) {
      nodes.push(value.slice(cursor, match.index));
    }
    nodes.push(
      <mark key={`tok-${String(match.index)}`} className="bg-info-50 text-info-800 rounded-sm">
        {match[0]}
      </mark>
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) {
    nodes.push(value.slice(cursor));
  }
  if (value.endsWith('\n')) {
    // Preserve the final empty line so wrapping stays in lockstep.
    nodes.push(<br key="trailing-newline" />);
  }
  return nodes;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

/**
 * Top-level page component for `/admin/components/:componentId/config`.
 *
 * The component is intentionally long but flat: a single function body
 * with clearly commented sections mirrors the section layout the admin
 * sees on screen. Splitting it further would fragment the state used by
 * the inline prop-label editor, the preview modal, and the confirm
 * modal — all three share the same `componentId` and toast hook.
 */
export function ComponentConfigPage() {
  const { componentId } = useParams<ComponentConfigRouteParams>();
  const resolvedComponentId = componentId ?? '';

  const registration = resolvedComponentId ? getComponent(resolvedComponentId) : undefined;

  const { toast } = useToast();

  // ---- State ---------------------------------------------------------

  /**
   * Id of the rule currently targeted by a destructive action. `null`
   * means the confirm modal is closed. Using an id (not the DTO) keeps
   * the state stable across list refetches.
   */
  const [ruleToDelete, setRuleToDelete] = useState<ComponentRuleDTO | null>(null);

  /**
   * Currently-edited prop pill, if any. We store the prop name so the
   * click handler can swap a single pill into its inline editor without
   * affecting the others.
   */
  const [editingPropName, setEditingPropName] = useState<string | null>(null);
  const [pendingLabelValue, setPendingLabelValue] = useState<string>('');

  /**
   * Translate response currently shown in the preview modal. When
   * non-null the modal is open and the "Save as draft" button is active.
   */
  const [pendingTranslation, setPendingTranslation] =
    useState<TranslateComponentRuleResponse | null>(null);

  /**
   * Rule whose raw JSON is currently being viewed / edited in the
   * `EditRuleJsonModal`. `null` means the modal is closed. We mirror the
   * pattern used by `ruleToDelete` and `pendingTranslation` above —
   * state-driven open / close, keyed on the DTO so we always have the
   * rule's id and status handy inside the modal.
   */
  const [editingRule, setEditingRule] = useState<ComponentRuleDTO | null>(null);

  /**
   * Draft layout effects authored by direct manipulation in the live
   * preview (rename / hide / reorder / tooltip). The single source of truth
   * shared between the {@link LivePreviewSection} (which runs `applyRules`
   * over a synthetic always-on rule built from these so the preview is
   * WYSIWYG) and the {@link LayoutAuthoringPanel} (which emits the edits).
   * Empty by default ⇒ the preview renders the component unchanged.
   */
  const [layoutEffects, setLayoutEffects] = useState<RuleEffect[]>([]);

  /**
   * Layout targets (fields / columns / sections) discovered by scanning the
   * live-preview DOM for the integrated components' stable `data-testid`
   * markers. Populated by {@link LivePreviewSection} after each render and
   * used as the **fallback** target source for components that have not yet
   * published a base config (see {@link configTargets} below).
   */
  const [layoutTargets, setLayoutTargets] = useState<LayoutTarget[]>([]);

  /**
   * Snapshot of the component's published base config, re-read from the
   * {@link layoutConfigStore} after each preview render. A migrated component
   * publishes its base config (via `usePublishLayoutConfig`) from the same
   * render the preview just committed, so re-reading on the preview's
   * "targets discovered" tick captures it. The store is a plain getter/setter
   * (no subscribe), so we mirror the DOM path and re-read on the same trigger.
   * `undefined` until a migrated component has rendered (the current reality
   * until Phase 5) ⇒ the editor falls back to the DOM-scraped targets.
   */
  const [publishedConfig, setPublishedConfig] = useState<PublishedLayoutConfig | undefined>(
    undefined
  );

  /**
   * Config-sourced editor targets, preferred over the DOM-scraped ones. Built
   * from the published base config so the editor sees title / actions /
   * placeholders / hidden fields / info items reliably (which the DOM scrape
   * cannot). Empty (`[]`) when nothing was published ⇒ the page falls back to
   * {@link layoutTargets}. Recomputed only when the published snapshot changes.
   */
  const configTargets = useMemo(() => discoverConfigTargets(publishedConfig), [publishedConfig]);

  /**
   * The targets actually fed to the authoring panel: the config-sourced list
   * when a migrated component published one, else the DOM-scrape fallback so
   * un-migrated components behave exactly as before.
   */
  const effectiveTargets = configTargets.length > 0 ? configTargets : layoutTargets;

  /**
   * Receive freshly-discovered targets from the preview. The discoverer
   * returns a *new* array on every preview render, so we diff a stable
   * signature and only commit when the set actually changed — otherwise the
   * state update would feed back into a redundant re-render.
   *
   * We also re-read the published base config from the store on this same tick
   * (a migrated component publishes it from the render the preview just
   * committed). Like the DOM-target diff, we only commit when the published
   * snapshot is a different reference so a stable publish doesn't churn state.
   */
  const handleTargetsDiscovered = (next: LayoutTarget[]) => {
    setLayoutTargets((prev) => {
      const sig = (ts: LayoutTarget[]) =>
        ts.map((t) => `${t.kind}:${t.key}:${t.baseLabel}`).join('|');
      return sig(prev) === sig(next) ? prev : next;
    });
    if (registration !== undefined) {
      const published = getLayoutConfig(registration.id);
      setPublishedConfig((prev) => (prev === published ? prev : published));
    }
  };

  /**
   * Fold one direct-manipulation gesture into the draft effect list. Wraps
   * the pure {@link applyEdit} reducer so the panel only emits intent and the
   * page owns the canonical list (one effect per slot, last-write-wins).
   */
  const handleLayoutEdit = (edit: LayoutEdit) => {
    setLayoutEffects((prev) => applyEdit(prev, edit));
  };

  /**
   * Reorderable **data rows** + manual-ordering support for the previewed
   * surface (data-row-ordering Tier 2). Read from the same resolved preview
   * props the live preview renders from (runtime capture ⇒ fallback
   * `previewProps`): a DataList over fully-loaded inline `data[]` with a stable
   * row id yields draggable rows; anything else (server-paginated, no stable
   * id, not a list) yields the documented disabled message. Memoised on the
   * registration id so it only recomputes when the previewed component changes.
   */
  const rowDiscovery = useMemo(() => {
    if (registration === undefined) {
      return discoverRowTargets(undefined);
    }
    const props = getRuntimeProps(registration.id) ?? registration.previewProps;
    return discoverRowTargets(props as Record<string, unknown> | undefined);
  }, [registration]);

  /**
   * Current textarea contents for the JSON editor. Seeded from
   * `editingRule.rule_json` (pretty-printed) when the modal opens, and
   * cleared when it closes. Kept as a controlled string so we can compare
   * against the original to gate the Save button.
   */
  const [editorValue, setEditorValue] = useState<string>('');

  /**
   * Most recent parse / validation error to surface above the textarea.
   * `null` hides the Alert. Reset to `null` on open and on every Save
   * attempt before validation runs.
   */
  const [editorError, setEditorError] = useState<string | null>(null);

  /**
   * Visibility picked in the JSON edit modal. Seeded from the rule's
   * current visibility on open; cleared to `null` on close so the next
   * open starts from the freshly-seeded value. Kept separate from
   * `editingRule` so an in-flight save doesn't touch the rule DTO.
   */
  const [editorVisibility, setEditorVisibility] = useState<'public' | 'private' | null>(null);

  /**
   * Rule whose `nl_input` is currently being re-edited in the
   * NL edit modal. `null` means the modal is closed. Drives the modal's
   * `open` prop and is the seed source for `nlEditValue`.
   *
   * Kept as a separate state slot from `editingRule` so the JSON-edit
   * flow and the NL-edit flow cannot collide — the two modals are
   * independently mounted and only one is open at any time.
   */
  const [editingNlRule, setEditingNlRule] = useState<ComponentRuleDTO | null>(null);

  /**
   * Controlled textarea contents for the NL edit modal. Seeded from
   * `editingNlRule.nl_input ?? ''` via a `useEffect` keyed on the rule
   * id so reopening the same rule keeps the in-progress text but
   * switching rules reseeds.
   */
  const [nlEditValue, setNlEditValue] = useState<string>('');

  /**
   * Translate response currently shown on top of the NL editor — when
   * non-null, the second `TranslatePreviewModal` is open. Distinct from
   * `pendingTranslation` so the create-flow preview cannot overlap with
   * the edit-flow preview.
   */
  const [nlEditPreview, setNlEditPreview] = useState<TranslateComponentRuleResponse | null>(null);

  /** Audit log panel expanded state. Collapsed by default (optional section). */
  const [auditLogExpanded, setAuditLogExpanded] = useState(false);

  /**
   * Rule visibility selected in the add-rule form. `'private'` is the
   * default and is omitted from the POST payload (the server treats that
   * as the implicit default); `'public'` is sent explicitly and requires
   * the current user to be on an admin tenant.
   */
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const isAdminTenant = useIsAdminTenant();

  /**
   * Active tenant id from the admin auth store. Seeds the target tenant
   * selector so admins who don't explicitly pick a different tenant fall
   * through to their own. Null in unauthenticated states (e.g. tests) —
   * the Save path falls back to omitting `tenant_id` in that case.
   */
  const activeTenantId = useAdminAuthStore((s) => s.activeTenantId);

  /**
   * The live app's active tenant captured at mount (a cross-module read — see
   * {@link getOriginTenantId}). Used to seed BOTH tenant selectors and the
   * preview-as picker so the admin starts on the tenant they were viewing in
   * the live module. Falls back to the admin auth store's active tenant when
   * nothing is stored. Read once via the lazy `useState` initialiser so a later
   * write to localStorage doesn't churn the seed.
   */
  const [originTenantId] = useState<string | null>(() => getOriginTenantId());

  /**
   * Target tenant for a Private rule when the author is an admin-tenant
   * user. Hidden from non-admin authors. Reset back to the active tenant
   * after a successful save so the next create defaults to the same
   * starting point. Seeded from the origin tenant (the tenant the admin was
   * viewing in the live app) so the default matches where they came from.
   */
  const [targetTenantId, setTargetTenantId] = useState<string | null>(
    originTenantId ?? activeTenantId
  );

  const tenantsQuery = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantsService.list(),
    staleTime: 5 * 60 * 1000,
    enabled: isAdminTenant,
  });

  const tenantOptions = useMemo(
    () =>
      (tenantsQuery.data?.data ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: String(t.id), label: t.name })),
    [tenantsQuery.data]
  );

  /**
   * Map of tenant id -> name for read-only captions (e.g. the JSON edit
   * modal where the tenant is locked but should still display by name).
   * Falls back to the raw id when the tenant has been deleted.
   */
  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenantsQuery.data?.data ?? []) {
      map.set(String(t.id), t.name);
    }
    return (id: string): string | undefined => map.get(id);
  }, [tenantsQuery.data]);

  const handleVisibilityChange = (next: 'public' | 'private') => {
    setVisibility(next);
    if (next === 'public') {
      // Public is tenant-less server-side; clear so a Private flip later
      // re-seeds from the active tenant instead of a stale prior pick.
      setTargetTenantId(null);
    } else if (targetTenantId === null) {
      setTargetTenantId(activeTenantId);
    }
  };

  /**
   * Visibility for the WYSIWYG "Edit layout" save path. Kept independent
   * from the NL form's `visibility` so toggling one surface never moves the
   * other. Defaults to `'private'` — the common case for a PM adapting one
   * tenant's UI — and is omitted from the payload (server-default).
   */
  const [layoutVisibility, setLayoutVisibility] = useState<'public' | 'private'>('private');

  /**
   * Target tenant for the WYSIWYG "Edit layout" save path. Seeded from the
   * origin tenant (the live app's active tenant) and reset back to the active
   * tenant after a successful save, mirroring {@link targetTenantId} but for
   * the independent layout surface.
   */
  const [layoutTargetTenantId, setLayoutTargetTenantId] = useState<string | null>(
    originTenantId ?? activeTenantId
  );

  /**
   * Tenant whose ACTIVE rules the live preview is rendered as ("Preview
   * component as <tenant>"). Independent from the save-target selectors: it
   * only changes which saved active rules are layered into the preview, not
   * where a new rule is saved. Seeded from the origin tenant so the preview
   * starts on the tenant the admin was viewing in the live app; back-filled by
   * the seeding effect below when it resolves async.
   */
  const [previewAsTenantId, setPreviewAsTenantId] = useState<string | null>(
    originTenantId ?? activeTenantId
  );

  /**
   * Optional name + description for the WYSIWYG "Edit layout" save path. The
   * admin can label a layout rule before saving it (e.g. "Hide coverage card")
   * so it's recognisable in the rules table; blank falls back to the generic
   * 'Layout adjustments' name. Reset after a successful save.
   */
  const [layoutRuleName, setLayoutRuleName] = useState<string>('');
  const [layoutRuleDescription, setLayoutRuleDescription] = useState<string>('');

  const handleLayoutVisibilityChange = (next: 'public' | 'private') => {
    setLayoutVisibility(next);
    if (next === 'public') {
      // Public is tenant-less server-side; clear so a Private flip later
      // re-seeds from the active tenant instead of a stale prior pick.
      setLayoutTargetTenantId(null);
    } else if (layoutTargetTenantId === null) {
      setLayoutTargetTenantId(activeTenantId);
    }
  };

  /**
   * Seed the tenant selectors once the active tenant resolves. The three
   * selectors (`targetTenantId`, `layoutTargetTenantId`, `previewAsTenantId`)
   * capture `originTenantId ?? activeTenantId` via `useState`, which can be
   * `null` on first render before the admin auth store hydrates async (and the
   * live app may not have stored an origin tenant). When a non-null fallback is
   * available we back-fill any selector still sitting on `null` — preferring
   * the origin tenant — but never clobber a value the user (or a Public→Private
   * flip) has already chosen.
   */
  useEffect(() => {
    const seed = originTenantId ?? activeTenantId;
    if (seed === null) return;
    setTargetTenantId((prev) => (prev === null ? seed : prev));
    setLayoutTargetTenantId((prev) => (prev === null ? seed : prev));
    setPreviewAsTenantId((prev) => (prev === null ? seed : prev));
  }, [activeTenantId, originTenantId]);

  /**
   * Ref onto the NL-input textarea. The page's `insertTokenAtCaret` helper
   * reads `selectionStart` / `selectionEnd` off this ref to decide where to
   * splice a newly-clicked `{{token}}`.
   */
  const nlTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Caret position to apply after the next render. Set when a token is
   * inserted programmatically; consumed in the `useEffect` below, which
   * restores focus and moves the caret immediately past the inserted token.
   *
   * We use a post-render `useEffect` (not `requestAnimationFrame`) because
   * the textarea's `value` is controlled, so React's DOM update must
   * complete before `setSelectionRange` sees the new content. `useEffect`
   * fires after commit and reads the freshly-rendered DOM.
   */
  const pendingCaretRef = useRef<number | null>(null);

  // ---- Queries -------------------------------------------------------

  const rulesQuery = useComponentRulesList(resolvedComponentId);
  const propLabelsQuery = useComponentPropLabels(resolvedComponentId);
  const auditLogQuery = useComponentRulesAuditLog(resolvedComponentId);

  // The tenant whose live preview we render: the explicit "Preview component
  // as" selection. The preview applies the active rules that THIS tenant would
  // see at runtime, independent of where a new rule would be saved.
  const previewTenantId = previewAsTenantId;

  // Active rules applicable to the previewed tenant (mapped to the evaluator
  // shape, sorted by priority asc). Reuses the existing `useComponentRulesList`
  // query data — the page already fetches all rules for the component for the
  // "existing rules" table — so the preview adds no extra request. Memoised on
  // the raw data + tenant so the child preview's `evaluated` memo only re-runs
  // when the fetched rules or the previewed tenant change.
  //
  // Declared here, among the page's hooks (before the unregistered-component
  // guard below), so the `useMemo` is never skipped on the early-return path —
  // a `useMemo` placed after that guard would violate the Rules of Hooks.
  const activePreviewRules = useMemo(
    () => selectActiveRulesForTenant(rulesQuery.data ?? [], previewTenantId),
    [rulesQuery.data, previewTenantId]
  );

  // ---- Mutations -----------------------------------------------------

  const translateMutation = useTranslateComponentRule();
  const createMutation = useCreateComponentRule();
  const updateRuleMutation = useUpdateComponentRule();
  const updateVisibilityMutation = useUpdateComponentRuleVisibility();
  const statusMutation = useUpdateComponentRuleStatus();
  const deleteMutation = useDeleteComponentRule();
  const upsertLabelMutation = useUpsertComponentPropLabel();

  // ---- Add-rule form -------------------------------------------------

  /**
   * Custom mutation function for the add-rule form. Instead of POSTing
   * to a create endpoint, it calls the translate service and hands the
   * result to the preview modal. The EntityForm hook interprets any
   * resolved value as "success" and fires `onSuccess`.
   */
  /**
   * Ref holding the latest `nlInput` string. `handleTranslate` closes over
   * this instead of reading `addRuleForm.values` directly — `addRuleForm`
   * is declared AFTER `handleTranslate`, so the closure would hit a
   * "used before declaration" TS error.
   */
  const nlInputRef = useRef<string>('');

  /**
   * Build the translate-endpoint payload for a given NL string. Single
   * source of truth shared by the create flow (`handleTranslate`) and
   * the NL-edit flow (`handleNlTranslate`) so they cannot drift on
   * `context_variables` / `props_schema` plumbing.
   *
   * Throws when the NL string is blank — both call sites already gate
   * on a non-empty textarea, but the throw is a defence-in-depth so an
   * accidental zero-length translator request surfaces as a developer
   * error rather than a silent server-side rejection.
   */
  const buildTranslatePayload = (nlInput: string) => {
    const trimmed = nlInput.trim();
    if (trimmed === '') {
      throw new Error('Please describe the rule in natural language');
    }
    // `context_variables` whitelists the variable names the translator is
    // allowed to emit, so the LLM cannot fabricate fields that won't exist
    // at runtime. Registration-time metadata lives on the component, not
    // the provider tree — the admin route has no access to the latter.
    const contextVariables =
      registration?.contextVariables !== undefined
        ? Object.keys(registration.contextVariables)
        : undefined;
    // `props_schema` whitelists the prop names the translator may target
    // with `setProp` / `filterPropValues` / `requireProp` effects, and now
    // carries the JSON Schema for each prop so the translator can
    // type-check the value it emits. Derived from the registration's Zod
    // schema via `z.toJSONSchema`. Omit the field when the component
    // declares no props (or its schema isn't introspectable as an object)
    // so the backend doesn't receive an empty object.
    const propsSchema = registration ? getPropJsonSchemasFromRegistration(registration) : undefined;
    // `filterable_props` whitelists the prop keys that legitimately support a
    // `filterPropValues` effect. The translator prefers `setProp` for props
    // outside this list. Omit the field when the component declares none so
    // the backend doesn't receive an empty array.
    const filterableProps = registration?.filterableProps;
    return {
      component_id: resolvedComponentId,
      nl_input: trimmed,
      ...(contextVariables !== undefined ? { context_variables: contextVariables } : {}),
      ...(propsSchema !== undefined ? { props_schema: propsSchema } : {}),
      ...(filterableProps !== undefined && filterableProps.length > 0
        ? { filterable_props: filterableProps }
        : {}),
    };
  };

  const handleTranslate = async (
    _payload: Record<string, unknown> | FormData
  ): Promise<TranslateComponentRuleResponse> => {
    // `nlInput` is NOT an EntityForm config field, so `buildPayload`
    // filters it out. Pull it off the ref that mirrors the form store.
    const result = await translateMutation.mutateAsync(buildTranslatePayload(nlInputRef.current));
    return result;
  };

  const addRuleForm = useEntityForm({
    config: addRuleFormConfig,
    mutationFn: handleTranslate,
    onSuccess: (response) => {
      setPendingTranslation(response as TranslateComponentRuleResponse);
    },
    onError: (error) => {
      toast({
        variant: 'error',
        title: 'Translation failed',
        message:
          error instanceof Error
            ? error.message
            : 'The translator could not parse the rule. Please rephrase and try again.',
      });
    },
  });

  // ---- NL input token insertion --------------------------------------

  /**
   * Current `nlInput` value pulled out of the form store. Used both to
   * compute the caret-aware insert position and to disable the submit
   * button when the textarea is empty. Falls back to `''` so the call
   * sites can treat it as a string unconditionally.
   */
  const nlInputValue =
    typeof addRuleForm.values.nlInput === 'string' ? (addRuleForm.values.nlInput as string) : '';
  // Keep the ref (read by `handleTranslate`) in sync with the form store.
  nlInputRef.current = nlInputValue;

  /**
   * Splice a `{{token}}` string into the NL textarea at the current caret
   * position (or append if the textarea isn't focused / has no selection).
   * Stores the target caret in `pendingCaretRef` so the post-render
   * `useEffect` below can restore focus and position.
   */
  const insertTokenAtCaret = (token: string) => {
    const wrapped = `{{${token}}}`;
    const textarea = nlTextareaRef.current;
    const current = nlInputValue;
    let start = current.length;
    let end = current.length;
    // Only trust the textarea's DOM selection when the textarea is the
    // active element. Some browsers return `0` / `value.length` on a blurred
    // textarea — combined with our splice math that collapses to a full
    // overwrite. Appending at end is the safe fallback.
    const isActive = textarea !== null && textarea === document.activeElement;
    if (isActive) {
      const selStart = textarea.selectionStart;
      const selEnd = textarea.selectionEnd;
      if (selStart !== null) start = selStart;
      if (selEnd !== null) end = selEnd;
    }
    const next = current.slice(0, start) + wrapped + current.slice(end);
    addRuleForm.setValue('nlInput', next);
    pendingCaretRef.current = start + wrapped.length;
  };

  // After a token insertion React re-renders with the new value; once the
  // textarea DOM reflects it we can safely move the caret + focus.
  useEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret === null) return;
    const textarea = nlTextareaRef.current;
    if (textarea === null) return;
    textarea.focus();
    textarea.setSelectionRange(caret, caret);
    pendingCaretRef.current = null;
  });

  // Seed / clear the JSON editor whenever `editingRule` transitions. When
  // a rule is selected, pretty-print its `rule_json` into the textarea,
  // seed the visibility selector, and clear any stale error. When the
  // modal closes (null) we reset everything so the next open starts from
  // a clean slate.
  useEffect(() => {
    if (editingRule === null) {
      setEditorValue('');
      setEditorError(null);
      setEditorVisibility(null);
      return;
    }
    setEditorValue(JSON.stringify(editingRule.rule_json, null, 2));
    setEditorError(null);
    setEditorVisibility(editingRule.visibility ?? 'private');
    // We intentionally key only on the id: opening a *different* rule
    // reseeds, but in-place edits to the same rule (e.g. a refetch
    // updates the DTO fields) must NOT wipe the admin's in-progress
    // textarea content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRule?.id]);

  // Seed / clear the NL-edit textarea whenever `editingNlRule`
  // transitions. Mirrors the JSON-edit seeding pattern above: we key
  // ONLY on the id so reopening the same rule preserves the admin's
  // in-progress text, but switching rules reseeds. On close we clear
  // the textarea so the next open starts from a clean slate.
  useEffect(() => {
    if (editingNlRule === null) {
      setNlEditValue('');
      return;
    }
    setNlEditValue(editingNlRule.nl_input ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingNlRule?.id]);

  // ---- Handlers ------------------------------------------------------

  /**
   * Persist the translated rule as a draft. Called from the preview
   * modal's confirm button. On success we clear the modal state and
   * reset the form so the admin can immediately write another rule.
   *
   * Not wrapped in `useCallback` because the React Compiler memoises
   * component bodies automatically; a manual callback here only
   * confused the compiler's dependency analysis.
   */
  const handleSaveDraft = () => {
    if (pendingTranslation === null) return;
    const stringValue = (key: string): string => {
      const raw = addRuleForm.values[key];
      return typeof raw === 'string' ? raw.trim() : '';
    };
    // Only include `visibility` when the admin opted into Public. The
    // server defaults the omitted field to `'private'`, which keeps the
    // wire payload narrow for the common case.
    const submittedVisibility = visibility;
    createMutation.mutate(
      {
        component_id: resolvedComponentId,
        name: stringValue('name'),
        description: stringValue('description') || null,
        nl_input: stringValue('nlInput'),
        rule_json: {
          conditions: pendingTranslation.rule.conditions,
          effects: pendingTranslation.rule.effects,
        },
        ...(submittedVisibility === 'public' ? { visibility: 'public' as const } : {}),
        // Only send `tenant_id` when the author is on an admin tenant,
        // they kept the rule Private, the tenant list query loaded
        // successfully, and the selector resolved to a non-null value.
        // The query-failure path deliberately omits the field so the
        // server falls back to inferring tenancy from the session — see
        // the "Tenant list unavailable" Alert above the Save button.
        ...(isAdminTenant &&
        submittedVisibility === 'private' &&
        !tenantsQuery.isError &&
        targetTenantId !== null
          ? { tenant_id: targetTenantId }
          : {}),
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: 'Rule saved as draft',
          });
          setPendingTranslation(null);
          addRuleForm.reset();
          setVisibility('private');
          setTargetTenantId(activeTenantId);
        },
        onError: (err) => {
          // Surface a targeted message when the server rejects a public
          // rule because the current tenant is not admin. We detect the
          // case via any of: HTTP 403, the documented error code, or a
          // message mentioning "visibility" — belt-and-braces so a
          // future backend tweak doesn't silently regress to the generic
          // toast.
          const errResponse = (
            err as { response?: { status?: number; data?: { error_code?: string } } }
          ).response;
          const errMessage = err instanceof Error ? err.message : '';
          const looksVisibilityForbidden =
            errResponse?.status === 403 ||
            errResponse?.data?.error_code === 'VISIBILITY_FORBIDDEN' ||
            errMessage.toLowerCase().includes('visibility');
          if (submittedVisibility === 'public' && looksVisibilityForbidden) {
            toast({
              variant: 'error',
              title: 'Failed to save rule',
              message: 'Public rules require an admin tenant.',
            });
            return;
          }
          toast({
            variant: 'error',
            title: 'Failed to save rule',
            message: err instanceof Error ? err.message : 'The server rejected the rule payload.',
          });
        },
      }
    );
  };

  /**
   * Persist the direct-manipulation layout edits as a single tenant-private
   * rule with an **always-on** condition (the empty-AND default). This is the
   * WYSIWYG save path — no NL round-trip. Scoping mirrors {@link handleSaveDraft}:
   * `visibility: 'private'` is the default the server infers, and `tenant_id`
   * is sent only when the author is on an admin tenant, the tenant list
   * loaded, and a target tenant resolved (otherwise the server infers tenancy
   * from the session).
   *
   * Saved as a `draft`; the admin activates it from the rules table just like
   * a translated rule, so the live-preview draft and the persisted lifecycle
   * stay consistent.
   */
  const handleSaveLayoutRule = () => {
    if (layoutEffects.length === 0) return;
    const ruleJson = buildLayoutRuleJson(layoutEffects);
    // Capture the chosen visibility for the error handler below — the
    // VISIBILITY_FORBIDDEN guard only fires for a Public attempt.
    const submittedVisibility = layoutVisibility;
    createMutation.mutate(
      {
        component_id: resolvedComponentId,
        name: layoutRuleName.trim() || 'Layout adjustments',
        description: layoutRuleDescription.trim() || null,
        nl_input: '',
        rule_json: ruleJson,
        // Only include `visibility` when the admin opted into Public. The
        // server defaults the omitted field to `'private'` — the common case
        // for a PM adapting one tenant's UI.
        ...(submittedVisibility === 'public' ? { visibility: 'public' as const } : {}),
        // Send `tenant_id` only when the author is on an admin tenant, kept
        // the rule Private, the tenant list loaded, and the selector resolved
        // to a non-null value. Otherwise the server infers tenancy from the
        // session (see the "Tenant list unavailable" Alert).
        ...(isAdminTenant &&
        submittedVisibility === 'private' &&
        !tenantsQuery.isError &&
        layoutTargetTenantId !== null
          ? { tenant_id: layoutTargetTenantId }
          : {}),
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: 'Layout rule saved as draft',
            message: 'Activate it from the rules table to apply it live.',
          });
          setLayoutEffects([]);
          setLayoutVisibility('private');
          setLayoutTargetTenantId(activeTenantId);
          setLayoutRuleName('');
          setLayoutRuleDescription('');
        },
        onError: (err) => {
          // Surface a targeted message when the server rejects a public rule
          // because the current tenant is not admin. Mirrors the NL path's
          // belt-and-braces detection (HTTP 403, the documented error code,
          // or a message mentioning "visibility"). Defensive: Public is
          // disabled for non-admins in the UI.
          const errResponse = (
            err as {
              response?: { status?: number; data?: { error_code?: string } };
            }
          ).response;
          const errMessage = err instanceof Error ? err.message : '';
          const looksVisibilityForbidden =
            errResponse?.status === 403 ||
            errResponse?.data?.error_code === 'VISIBILITY_FORBIDDEN' ||
            errMessage.toLowerCase().includes('visibility');
          if (submittedVisibility === 'public' && looksVisibilityForbidden) {
            toast({
              variant: 'error',
              title: 'Failed to save layout rule',
              message: 'Public rules require an admin tenant.',
            });
            return;
          }
          toast({
            variant: 'error',
            title: 'Failed to save layout rule',
            message: err instanceof Error ? err.message : 'The server rejected the rule payload.',
          });
        },
      }
    );
  };

  const handleActivate = (rule: ComponentRuleDTO) => {
    statusMutation.mutate(
      { id: rule.id, status: 'active', componentId: resolvedComponentId },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Rule activated' });
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to activate rule',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  const handleArchive = (rule: ComponentRuleDTO) => {
    statusMutation.mutate(
      { id: rule.id, status: 'archived', componentId: resolvedComponentId },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Rule archived' });
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to archive rule',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  const handleConfirmDelete = () => {
    if (ruleToDelete === null) return;
    deleteMutation.mutate(
      { id: ruleToDelete.id, componentId: resolvedComponentId },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Rule deleted' });
          setRuleToDelete(null);
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to delete rule',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  /**
   * Parse + validate the JSON editor textarea, then PUT the new
   * `rule_json` onto the current `editingRule`. Surfaces parse errors
   * and schema errors inline via `editorError` so the admin can fix
   * typos without closing the modal.
   */
  const handleSaveRuleJson = () => {
    if (editingRule === null) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(editorValue);
    } catch (err) {
      setEditorError(err instanceof Error ? err.message : 'Invalid JSON');
      return;
    }
    const result = ruleJsonSchema.safeParse(parsed);
    if (!result.success) {
      const issue = result.error.issues[0];
      const path = issue ? issue.path.join('.') : '';
      setEditorError(
        issue ? `${path !== '' ? path + ': ' : ''}${issue.message}` : 'Invalid rule JSON'
      );
      return;
    }
    // The general PUT endpoint does not accept `visibility` — it's
    // routed through a dedicated PATCH. Build a content-only payload
    // and diff the scope client-side; the PATCH only fires when it
    // actually changed (also keeps the server's 422-on-same-to-same
    // guard out of the way).
    const submittedVisibility = editorVisibility;
    const visibilityChanged =
      submittedVisibility !== null && submittedVisibility !== editingRule.visibility;
    const payload: UpdateComponentRulePayload = { rule_json: result.data };
    updateRuleMutation.mutate(
      {
        id: editingRule.id,
        componentId: resolvedComponentId,
        payload,
      },
      {
        onSuccess: () => {
          // If visibility is unchanged we're done after the PUT —
          // toast success and close the modal. Otherwise chain the
          // visibility PATCH; toast + close fire only after both
          // succeed so a visibility failure keeps the modal open with
          // the targeted error message.
          if (!visibilityChanged || submittedVisibility === null) {
            toast({ variant: 'success', title: 'Rule updated' });
            setEditingRule(null);
            return;
          }
          updateVisibilityMutation.mutate(
            {
              id: editingRule.id,
              visibility: submittedVisibility,
              componentId: resolvedComponentId,
            },
            {
              onSuccess: () => {
                toast({ variant: 'success', title: 'Rule updated' });
                setEditingRule(null);
              },
              onError: (err) => {
                // Same belt-and-braces detection as the create path:
                // 403, the documented error code, or a "visibility"
                // mention in the message. The PATCH is the only call
                // that can legitimately hit VISIBILITY_FORBIDDEN, so
                // we don't need to gate on the submitted value here.
                const errResponse = (
                  err as {
                    response?: {
                      status?: number;
                      data?: { error_code?: string };
                    };
                  }
                ).response;
                const errMessage = err instanceof Error ? err.message : '';
                const looksVisibilityForbidden =
                  errResponse?.status === 403 ||
                  errResponse?.data?.error_code === 'VISIBILITY_FORBIDDEN' ||
                  errMessage.toLowerCase().includes('visibility');
                if (looksVisibilityForbidden) {
                  toast({
                    variant: 'error',
                    title: 'Failed to update rule',
                    message: 'Public rules require an admin tenant.',
                  });
                  return;
                }
                toast({
                  variant: 'error',
                  title: 'Failed to update rule',
                  message: err instanceof Error ? err.message : 'Unexpected error.',
                });
              },
            }
          );
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to update rule',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  /**
   * Open the NL edit modal for `rule`. The seeding `useEffect` keyed on
   * `editingNlRule?.id` populates `nlEditValue` from `rule.nl_input` —
   * we do NOT seed inline here so the seeding logic stays in one place
   * and reopening the same rule keeps in-progress text intact.
   */
  const handleEditNl = (rule: ComponentRuleDTO) => {
    setEditingNlRule(rule);
    setNlEditValue(rule.nl_input ?? '');
  };

  /**
   * Re-translate the edited NL string and stash the result in
   * `nlEditPreview`, which opens the second `TranslatePreviewModal` on
   * top of the editor. Errors surface via the existing translate
   * mutation's onError toast pattern; the editor stays open so the
   * admin can fix the sentence and retry.
   */
  const handleNlTranslate = async () => {
    try {
      const response = await translateMutation.mutateAsync(buildTranslatePayload(nlEditValue));
      setNlEditPreview(response);
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Translation failed',
        message:
          err instanceof Error
            ? err.message
            : 'The translator could not parse the rule. Please rephrase and try again.',
      });
    }
  };

  /**
   * Persist the re-translated NL edit. Sends EXACTLY `{ rule_json,
   * nl_input }` — no `name`, `description`, `priority`, `status`, or
   * `visibility`. Visibility in particular MUST stay out: the PUT
   * endpoint silently drops it (see `UpdateComponentRulePayload` JSDoc
   * in `componentRulesService.ts`).
   *
   * On success: clear all three NL-edit state slots and toast. On
   * error: leave the editor + preview open so the admin can retry.
   */
  const handleSaveNlEditedRule = () => {
    if (editingNlRule === null || nlEditPreview === null) return;
    updateRuleMutation.mutate(
      {
        id: editingNlRule.id,
        componentId: resolvedComponentId,
        payload: {
          rule_json: {
            conditions: nlEditPreview.rule.conditions,
            effects: nlEditPreview.rule.effects,
          },
          nl_input: nlEditValue.trim(),
        },
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Rule updated' });
          setEditingNlRule(null);
          setNlEditValue('');
          setNlEditPreview(null);
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to update rule',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  /**
   * Close the NL edit modal without firing any mutation. Clears all
   * three NL-edit state slots — including the preview — so the next
   * open starts from a clean slate.
   */
  const handleCancelNlEdit = () => {
    setEditingNlRule(null);
    setNlEditValue('');
    setNlEditPreview(null);
  };

  const handleStartLabelEdit = (propName: string, currentLabel: string) => {
    setEditingPropName(propName);
    setPendingLabelValue(currentLabel);
  };

  const handleCancelLabelEdit = () => {
    setEditingPropName(null);
    setPendingLabelValue('');
  };

  const handleSaveLabel = (propName: string) => {
    const nextLabel = pendingLabelValue.trim();
    if (nextLabel === '') {
      toast({
        variant: 'warning',
        title: 'Label cannot be empty',
      });
      return;
    }
    upsertLabelMutation.mutate(
      {
        componentId: resolvedComponentId,
        propName,
        displayLabel: nextLabel,
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Label updated' });
          setEditingPropName(null);
          setPendingLabelValue('');
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to update label',
            message: err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      }
    );
  };

  // ---- Unregistered component guard ---------------------------------

  if (registration === undefined) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Components</Breadcrumb.Item>
        </Breadcrumb>
        <Text as="h1" size="2xl" weight="bold">
          Component configuration
        </Text>
        <Alert variant="error" title="Component not registered">
          {`Component "${resolvedComponentId}" is not registered. `}
          Make sure the module calls its registration on startup (see
          <Text as="span" weight="semibold">
            {' src/modules/<module>/components/register.ts'}
          </Text>
          ).
        </Alert>
      </VStack>
    );
  }

  // ---- Derived data ---------------------------------------------------

  const rules = rulesQuery.data ?? [];

  const propLabelsByName = new Map<string, ComponentPropLabelDTO>();
  for (const label of propLabelsQuery.data ?? []) {
    propLabelsByName.set(label.prop_name, label);
  }
  const propNames = getPropNamesFromRegistration(registration);

  return (
    <VStack gap={6}>
      {/* Breadcrumbs + header */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin">Components</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{registration.displayName}</Breadcrumb.Item>
      </Breadcrumb>

      <HStack justify="between" align="center">
        <VStack gap={1}>
          <Text as="h1" size="2xl" weight="bold">
            {`Configure: ${registration.displayName}`}
          </Text>
          <Text size="sm" color="muted">
            {registration.id}
          </Text>
        </VStack>
      </HStack>

      {/*
        Working area: a scrollable middle column of editing controls beside a
        fixed live-preview column on the right (lg+). The preview is `sticky`,
        so it stays in view while the middle column scrolls — edit on the left,
        watch the result update on the right. The two stack vertically on
        narrow screens. The preview itself is the right column further down.
      */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Middle column: scrollable editing controls */}
        <VStack gap={6} className="min-w-0 flex-1">
          {/* ---- Section A2: Direct-manipulation layout authoring ---- */}
          <Card>
            <Card.Header
              title="Edit layout"
              subtitle="Rename, hide, reorder, or add tooltips to fields, columns, and sections by direct manipulation. Changes preview live above. Use natural language below for conditional rules."
            />
            <Card.Content>
              <VStack gap={4}>
                <LayoutAuthoringPanel
                  targets={effectiveTargets}
                  effects={layoutEffects}
                  onEdit={handleLayoutEdit}
                  rowTargets={rowDiscovery.rowTargets}
                  rowReorderSupport={rowDiscovery.support}
                />
                {layoutEffects.length > 0 && (
                  <VStack gap={2}>
                    {/* Name + description — let the admin label the layout rule so
                    it's recognisable in the rules table. Both optional: a blank
                    name falls back to the generic 'Layout adjustments'. Uses
                    the base TextField (single-line) — the catalog has no
                    textarea base component, so the description is a plain
                    TextField too. */}
                    <TextField
                      label="Rule name"
                      placeholder="Layout adjustments"
                      value={layoutRuleName}
                      onChange={(e) => {
                        setLayoutRuleName(e.target.value);
                      }}
                    />
                    <TextField
                      label="Description"
                      placeholder="Optional notes about why this rule exists"
                      value={layoutRuleDescription}
                      onChange={(e) => {
                        setLayoutRuleDescription(e.target.value);
                      }}
                    />
                    {/* Scope controls — independent from the NL form's selectors.
                    Public is gated on admin-tenant status (the option is
                    disabled for non-admins); the tenant picker only shows for
                    an admin authoring a Private rule. */}
                    <VisibilityRadioGroup
                      value={layoutVisibility}
                      onChange={handleLayoutVisibilityChange}
                      isAdminTenant={isAdminTenant}
                    />
                    {isAdminTenant && layoutVisibility === 'private' && (
                      <TargetTenantSelect
                        value={layoutTargetTenantId}
                        onChange={setLayoutTargetTenantId}
                        options={tenantOptions}
                        isLoading={tenantsQuery.isLoading}
                        isError={tenantsQuery.isError}
                      />
                    )}
                    <HStack gap={2} justify="end" align="center">
                      <Text size="sm" color="muted">
                        {`${String(layoutEffects.length)} change${layoutEffects.length === 1 ? '' : 's'} pending`}
                      </Text>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setLayoutEffects([]);
                        }}
                        disabled={createMutation.isPending}
                      >
                        Discard
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveLayoutRule}
                        loading={createMutation.isPending}
                      >
                        {`Save layout rule (${layoutVisibility})`}
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </Card.Content>
          </Card>

          {/* ---- Section B: Existing rules ---- */}
          <Card>
            <Card.Header
              title="Existing rules"
              subtitle={`Rules applied to ${registration.displayName}.`}
            />
            <Card.Content>
              <ExistingRulesSection
                rulesQuery={rulesQuery}
                rules={rules}
                onActivate={handleActivate}
                onArchive={handleArchive}
                onEditJson={setEditingRule}
                onEditNl={handleEditNl}
                onRequestDelete={setRuleToDelete}
              />
            </Card.Content>
          </Card>

          {/* ---- Section C: Prop labels (click-to-insert above the form) ---- */}
          <Card>
            <Card.Header
              title="Prop labels"
              subtitle="Click a pill to insert the prop token into the rule below. Click the pencil to rename a prop for admins in this tenant."
            />
            <Card.Content>
              {propLabelsQuery.isLoading ? (
                <HStack gap={2} align="center">
                  <Spinner size="sm" />
                  <Text size="sm" color="muted">
                    Loading labels...
                  </Text>
                </HStack>
              ) : (
                <HStack gap={2} wrap>
                  {propNames.length === 0 && (
                    <Text size="sm" color="muted">
                      No props declared for this component.
                    </Text>
                  )}
                  {propNames.map((propName) => {
                    const storedLabel = propLabelsByName.get(propName);
                    // Registry declares `defaultPropLabels` as
                    // `Partial<Record<keyof P & string, string>>`, which
                    // after widening becomes `Partial<Record<never, string>>`
                    // at the registry boundary. Cast to the wider indexable
                    // type here so the prop name lookup is legal.
                    const defaultLabels = registration.defaultPropLabels as Record<
                      string,
                      string | undefined
                    >;
                    const defaultLabel = defaultLabels[propName] ?? propName;
                    const currentLabel = storedLabel?.display_label ?? defaultLabel;
                    const isEditing = editingPropName === propName;

                    if (isEditing) {
                      return (
                        <HStack key={propName} gap={2} align="center">
                          <TextField
                            value={pendingLabelValue}
                            onChange={(e) => {
                              setPendingLabelValue(e.target.value);
                            }}
                            aria-label={`Edit label for ${propName}`}
                            size="sm"
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              handleSaveLabel(propName);
                            }}
                            loading={upsertLabelMutation.isPending}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelLabelEdit}
                            disabled={upsertLabelMutation.isPending}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      );
                    }

                    return (
                      <HStack key={propName} gap={1} align="center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          onClick={() => {
                            insertTokenAtCaret(propName);
                          }}
                          aria-label={`Insert ${propName} token`}
                        >
                          <Badge variant="info" size="sm" pill>
                            {`${propName}: ${currentLabel}`}
                          </Badge>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            handleStartLabelEdit(propName, currentLabel);
                          }}
                          aria-label={`Edit label for ${propName}`}
                          leftIcon={<Icon icon={Pencil} size="xs" />}
                        />
                      </HStack>
                    );
                  })}
                </HStack>
              )}
            </Card.Content>
          </Card>

          {/* ---- Section C2: Available context variables (click-to-insert) ---- */}
          <Card>
            <Card.Header
              title="Available context variables"
              subtitle="Click a variable to insert it as a token into the rule below."
            />
            <Card.Content>
              <ContextVariablesSection
                registration={registration}
                onInsert={insertTokenAtCaret}
                capture={getRuntimeContext(registration.id)}
              />
            </Card.Content>
          </Card>

          {/* ---- Section D: Add a new rule ---- */}
          <Card>
            <Card.Header
              title="Add a new rule"
              subtitle="Describe the rule in natural language; preview the parsed form before saving."
            />
            <Card.Content>
              <EntityForm config={addRuleFormConfig} form={addRuleForm}>
                <EntityForm.Alert />
                <EntityForm.Fields />
                {/* NL input lives outside the EntityForm config so the backdrop
                overlay can render highlighted `{{tokens}}` under the
                textarea. The submit button below is also custom so we can
                disable it when the textarea is empty (nlInput is no longer
                a config-level required field). */}
                <VStack gap={1} className="mt-2">
                  <Text as="label" size="sm" weight="medium" htmlFor="component-rules-nl-input">
                    Describe the rule in natural language
                  </Text>
                  <NLInputWithHighlights
                    ref={nlTextareaRef}
                    id="component-rules-nl-input"
                    ariaLabel="Describe the rule in natural language"
                    value={nlInputValue}
                    onChange={(next) => {
                      addRuleForm.setValue('nlInput', next);
                    }}
                    maxLength={1000}
                    placeholder="Example: hide if task_type is not Declaration or Constat"
                  />
                  <Text size="sm" color="muted">
                    This text is sent to the translator. You will review the parsed rule before it
                    is saved.
                  </Text>
                </VStack>
                {/* Visibility selector. Public is gated on admin-tenant status —
                non-admin tenants can still see the control (so the caption
                makes sense) but the Public option is disabled and wrapped
                in a Tooltip explaining why. */}
                <VisibilityRadioGroup
                  value={visibility}
                  onChange={handleVisibilityChange}
                  isAdminTenant={isAdminTenant}
                />
                {isAdminTenant && visibility === 'private' && (
                  <TargetTenantSelect
                    value={targetTenantId}
                    onChange={setTargetTenantId}
                    options={tenantOptions}
                    isLoading={tenantsQuery.isLoading}
                    isError={tenantsQuery.isError}
                  />
                )}
                <HStack justify="end" gap={2} className="mt-4">
                  <Button
                    type="button"
                    variant="primary"
                    loading={addRuleForm.isSubmitting}
                    disabled={addRuleForm.isSubmitting || nlInputValue === ''}
                    onClick={() => {
                      // Pre-flight empty-input guard — EntityForm validation
                      // doesn't know about `nlInput` (it's not a config field),
                      // so we check here before kicking off submit.
                      if (nlInputValue.trim() === '') {
                        toast({
                          variant: 'error',
                          title: 'Translation failed',
                          message: 'Please describe the rule in natural language',
                        });
                        return;
                      }
                      void addRuleForm.submit();
                    }}
                  >
                    Translate and preview
                  </Button>
                </HStack>
              </EntityForm>
            </Card.Content>
          </Card>

          {/* ---- Section E (optional): Audit log ---- */}
          <Panel padding="compact" variant="outlined">
            <HStack justify="between" align="center">
              <Text as="h2" size="lg" weight="semibold">
                Audit log
              </Text>
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<Icon icon={ChevronDown} size="sm" />}
                onClick={() => {
                  setAuditLogExpanded((prev) => !prev);
                }}
                aria-expanded={auditLogExpanded}
              >
                {auditLogExpanded ? 'Hide' : 'Show'}
              </Button>
            </HStack>
            {auditLogExpanded && (
              <div className="mt-4">
                {auditLogQuery.isLoading ? (
                  <HStack gap={2} align="center">
                    <Spinner size="sm" />
                    <Text size="sm" color="muted">
                      Loading audit log...
                    </Text>
                  </HStack>
                ) : auditLogQuery.isError ? (
                  <Alert variant="error" title="Failed to load audit log">
                    {auditLogQuery.error instanceof Error
                      ? auditLogQuery.error.message
                      : 'Unexpected error.'}
                  </Alert>
                ) : (auditLogQuery.data ?? []).length === 0 ? (
                  <Alert variant="info" title="No audit events yet">
                    Translation, save, activate, archive, and delete actions will appear here.
                  </Alert>
                ) : (
                  <Table size="compact">
                    <Table.Head>
                      <Table.Row>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                        <Table.HeaderCell>Action</Table.HeaderCell>
                        <Table.HeaderCell>User</Table.HeaderCell>
                      </Table.Row>
                    </Table.Head>
                    <Table.Body>
                      {(auditLogQuery.data ?? []).slice(0, 20).map((entry) => (
                        <Table.Row key={entry.id}>
                          <Table.Cell>
                            <Text size="sm">
                              {entry.created_at !== null
                                ? new Date(entry.created_at).toLocaleString()
                                : '--'}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge size="sm" variant="info">
                              {entry.action}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="sm" color="muted">
                              {entry.user_id ?? '--'}
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </div>
            )}
          </Panel>
        </VStack>

        {/* Right column: fixed live preview. `sticky` on lg+ keeps it in view
            while the middle column scrolls; it is wide enough for a real
            component render (scaling up on larger screens) and scrolls
            internally when the preview is taller than the viewport. */}
        <div className="w-full shrink-0 self-start lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:w-[440px] lg:overflow-y-auto xl:w-[560px] 2xl:w-[640px]">
          <LivePreviewSection
            registration={registration}
            activeRules={activePreviewRules}
            layoutEffects={layoutEffects}
            onTargetsDiscovered={handleTargetsDiscovered}
            previewAsTenantId={previewAsTenantId}
            onPreviewAsTenantChange={setPreviewAsTenantId}
            tenantOptions={tenantOptions}
            tenantsLoading={tenantsQuery.isLoading}
          />
        </div>
      </div>

      {/* ---- Preview modal ---- */}
      <TranslatePreviewModal
        pendingTranslation={pendingTranslation}
        registration={registration}
        isSaving={createMutation.isPending}
        onCancel={() => {
          setPendingTranslation(null);
        }}
        onSave={handleSaveDraft}
      />

      {/* ---- Edit rule JSON modal ---- */}
      <EditRuleJsonModal
        rule={editingRule}
        value={editorValue}
        onChange={setEditorValue}
        visibility={editorVisibility}
        onVisibilityChange={setEditorVisibility}
        isAdminTenant={isAdminTenant}
        tenantNameById={tenantNameById}
        error={editorError}
        isSaving={updateRuleMutation.isPending || updateVisibilityMutation.isPending}
        onCancel={() => {
          setEditingRule(null);
        }}
        onSave={handleSaveRuleJson}
        saveDisabled={
          editingRule === null ||
          (editorValue === JSON.stringify(editingRule.rule_json, null, 2) &&
            editorVisibility === editingRule.visibility)
        }
      />

      {/* ---- Edit rule NL modal ---- */}
      <Modal open={editingNlRule !== null} onClose={handleCancelNlEdit} size="lg">
        <Modal.Header title="Edit rule (NL)">
          {editingNlRule !== null && (
            <HStack gap={2} align="center" className="mt-1">
              <Text size="sm" color="muted">
                {editingNlRule.name}
              </Text>
              <Badge variant={statusBadgeVariant(editingNlRule.status)} size="sm">
                {editingNlRule.status}
              </Badge>
            </HStack>
          )}
        </Modal.Header>
        <Modal.Body>
          <VStack gap={2}>
            <Text as="label" size="sm" weight="medium" htmlFor="component-rules-nl-edit-input">
              Describe the rule in natural language
            </Text>
            <textarea
              id="component-rules-nl-edit-input"
              aria-label="Edit rule NL input"
              value={nlEditValue}
              onChange={(e) => {
                setNlEditValue(e.target.value);
              }}
              rows={6}
              maxLength={1000}
              className="w-full font-sans text-sm leading-6 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-primary-400 resize-y"
            />
            <Text size="sm" color="muted">
              The edited text will be re-translated. You will review the parsed rule before it is
              saved.
            </Text>
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={handleCancelNlEdit}
              disabled={translateMutation.isPending || updateRuleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={translateMutation.isPending}
              disabled={nlEditValue.trim() === '' || translateMutation.isPending}
              onClick={() => {
                void handleNlTranslate();
              }}
            >
              Regenerate JSON
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>

      {/* ---- NL edit preview modal (second TranslatePreviewModal instance) ---- */}
      <TranslatePreviewModal
        pendingTranslation={nlEditPreview}
        registration={registration}
        isSaving={updateRuleMutation.isPending}
        onCancel={() => {
          setNlEditPreview(null);
        }}
        onSave={handleSaveNlEditedRule}
      />

      {/* ---- Delete confirm modal ---- */}
      <Modal
        open={ruleToDelete !== null}
        onClose={() => {
          setRuleToDelete(null);
        }}
        size="sm"
      >
        <Modal.Header title="Delete rule" />
        <Modal.Body>
          <Text>
            Are you sure you want to delete{' '}
            <Text as="span" weight="bold">
              {ruleToDelete?.name ?? ''}
            </Text>
            ? This cannot be undone.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={() => {
                setRuleToDelete(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </VStack>
  );
}

export default ComponentConfigPage;

// ---------------------------------------------------------------------------
// Sub-section: Live preview
// ---------------------------------------------------------------------------

interface LivePreviewSectionProps {
  readonly registration: ComponentRegistration<unknown>;
  /**
   * Saved **active** rules that apply to the previewed tenant, already mapped
   * to the evaluator shape and sorted by `priority` ascending (see
   * `selectActiveRulesForTenant`). The preview applies these FIRST so it shows
   * the component exactly as the previewed tenant sees it at runtime, then
   * layers the unsaved draft edits on top.
   */
  readonly activeRules: readonly ComponentRule[];
  /**
   * Draft layout effects authored upstream. The preview runs the real engine
   * (`applyRules`) over a synthetic always-on rule built from these so the
   * rendered component reflects the edits **live** (WYSIWYG) — exactly the
   * runtime path the `withComponentRules` HOC takes, minus the fetch. The
   * draft rule is appended LAST so the unsaved edits win on conflicts with the
   * saved active rules.
   */
  readonly layoutEffects: readonly RuleEffect[];
  /**
   * Called after each render with the layout targets discovered by scanning
   * the rendered preview DOM. Lets the parent feed the authoring panel
   * without the panel reaching into the component tree.
   */
  readonly onTargetsDiscovered: (targets: LayoutTarget[]) => void;
  /**
   * Tenant the preview is rendered AS — drives which saved active rules are
   * layered in (the parent filters `activeRules` by this tenant). `null`
   * before a tenant resolves.
   */
  readonly previewAsTenantId: string | null;
  /** Change the "preview as" tenant. */
  readonly onPreviewAsTenantChange: (id: string) => void;
  /** Tenant options for the "Preview component as" selector. */
  readonly tenantOptions: ReadonlyArray<{ value: string; label: string }>;
  /** Whether the tenant list is still loading (selector shows a placeholder). */
  readonly tenantsLoading: boolean;
}

/**
 * Renders the registered component inside an error boundary, using the
 * most recently captured runtime props or falling back to `previewProps`.
 *
 * WYSIWYG: rather than rendering the component with its raw props, the
 * preview runs {@link applyRules} over a synthetic always-on rule built from
 * the upstream `layoutEffects`. The resulting `props` (mutated by any
 * `setProp` / `filterPropValues`) and accumulated `_layoutOverrides` (from
 * the rename / hide / reorder / tooltip effects) are passed to the component
 * — the same prop the runtime HOC injects — so a migrated component reflects
 * the edits exactly as an end user would see them.
 *
 * After every render it scans the preview container for the integrated
 * components' stable `data-testid` markers and hands the discovered targets
 * up via `onTargetsDiscovered`.
 */
function LivePreviewSection({
  registration,
  activeRules,
  layoutEffects,
  onTargetsDiscovered,
  previewAsTenantId,
  onPreviewAsTenantChange,
  tenantOptions,
  tenantsLoading,
}: Readonly<LivePreviewSectionProps>) {
  const runtimeProps = getRuntimeProps(registration.id);
  const usingFallback = runtimeProps === undefined;
  const resolvedProps = runtimeProps !== undefined ? runtimeProps : registration.previewProps;

  const Component = registration.component;

  const previewRef = useRef<HTMLDivElement | null>(null);

  // Run the evaluator over [saved active rules …, draft rule] so the preview
  // is WYSIWYG: it first applies every active rule the previewed tenant sees
  // at runtime (in priority-ascending order, exactly as the `withComponentRules`
  // HOC does), then layers the synthetic always-on draft rule LAST so the
  // unsaved edits win on conflicts.
  //
  // `applyRules` re-sorts by `priority` ASC (ties by `id`), so array position
  // does NOT decide precedence — priority does. The draft therefore takes a
  // priority strictly ABOVE every active rule (`max + 1`, ≥ 1) so it is folded
  // last and its last-write-wins on any key an active rule also touches. (With
  // a fixed `priority: 0` it could otherwise tie-break ahead of an active rule
  // and lose.)
  //
  // Context bag is `{}`: `LivePreviewSection` has no captured preview context,
  // and layout rules — the direct-manipulation use case — are authored
  // ALWAYS_ON (empty AND), so they fire regardless of context. A *conditional*
  // active rule that branches on context (e.g. `task_type`) is included in the
  // list but its condition won't fire here without a context bag; that matches
  // "preview the unconditional layout".
  //
  // With no active rules AND no draft effects the list is a single empty-effect
  // draft rule, so `applyRules` returns the props unchanged with no
  // `_layoutOverrides` — the component renders exactly as before (no regression).
  const evaluated = useMemo(() => {
    const base = (resolvedProps ?? {}) as Record<string, unknown>;
    const maxActivePriority = activeRules.reduce((max, rule) => Math.max(max, rule.priority), 0);
    const draftRule: ComponentRule = {
      ...buildDraftRule(registration.id, layoutEffects),
      priority: maxActivePriority + 1,
    };
    return applyRules(base, [...activeRules, draftRule], {});
  }, [resolvedProps, activeRules, layoutEffects, registration.id]);

  // Discover layout targets after the preview commits. We re-scan whenever the
  // evaluated output changes (a new effect can reveal/hide markers). Comparing
  // a stable signature avoids spamming the parent setter with equal arrays.
  useEffect(() => {
    const discovered = discoverLayoutTargets(previewRef.current);
    onTargetsDiscovered(discovered);
    // `resolvedProps` / `evaluated` drive the rendered DOM; re-scan on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluated, resolvedProps]);

  const combinedProps: Record<string, unknown> = {
    ...evaluated.props,
    ...(evaluated.layoutOverrides !== undefined
      ? { _layoutOverrides: evaluated.layoutOverrides }
      : {}),
  };

  return (
    <Card>
      <Card.Header
        title="Live preview"
        subtitle={`Rendered from the ${registration.module} module using ${usingFallback ? 'fallback' : 'runtime-captured'} props (evaluator applied).`}
      />
      <Card.Content>
        <VStack gap={3}>
          {/* "Preview component as <tenant>" — changes which saved active rules
              are layered into the preview (not where rules are saved). Visible
              regardless of draft edits; disabled with a loading placeholder
              while the tenant list resolves. */}
          <Select
            label="Preview component as"
            searchable
            options={tenantOptions as { value: string; label: string }[]}
            value={previewAsTenantId ?? ''}
            onChange={(v) => {
              if (typeof v === 'string') onPreviewAsTenantChange(v);
            }}
            disabled={tenantsLoading || tenantOptions.length === 0}
            placeholder={
              tenantsLoading || tenantOptions.length === 0
                ? 'Loading tenants...'
                : 'Select a tenant to preview as'
            }
          />
          {resolvedProps === undefined ? (
            <Alert variant="info" title="No preview available">
              No live capture and no fallback <code>previewProps</code> defined for this component.
              Open the config page via CMD+click from a page that renders it, or register the
              component with a <code>previewProps</code> sample.
            </Alert>
          ) : (
            <VStack gap={3}>
              {usingFallback && (
                <Alert variant="info" title="Using fallback props">
                  Preview uses the registry&apos;s fallback <code>previewProps</code> — no runtime
                  capture is available. For a live preview, open this page via CMD+click from a page
                  where the component is rendered.
                </Alert>
              )}
              <div ref={previewRef} data-testid="live-preview-root">
                <PreviewErrorBoundary>
                  {/*
                  `combinedProps` is the evaluator output (props mutated by
                  any `setProp` / `filterPropValues`) plus the `_layoutOverrides`
                  sentinel the runtime HOC also injects. The registry only
                  stores values whose shape matches the component's props
                  schema, so spreading the record is safe here.
                */}
                  <Component {...combinedProps} />
                </PreviewErrorBoundary>
              </div>
            </VStack>
          )}
        </VStack>
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Available context variables
// ---------------------------------------------------------------------------

interface ContextVariablesSectionProps {
  readonly registration: ComponentRegistration<unknown>;
  readonly onInsert?: (variableName: string) => void;
  /**
   * Last captured runtime rule-context bag for this component. When
   * present, each variable row shows its live value next to the badge
   * and a caption above the list reports when it was captured.
   */
  readonly capture?: RuntimeContextCapture;
}

/**
 * Renders one `LiveContextRow` per rule-context variable the hosting
 * page exposes, with the variable's most recent live value (from
 * `runtimeContextStore`) shown next to the existing badge. Falls back to
 * a muted message when the registration does not declare any context
 * variables; shows a muted "no live capture yet" caption when nothing
 * has been captured for this component.
 *
 * When `onInsert` is provided each badge becomes a ghost `Button` that
 * inserts the variable name as a `{{token}}` into the NL rule textarea
 * via the parent page's `insertTokenAtCaret` helper.
 */
function ContextVariablesSection({
  registration,
  onInsert,
  capture,
}: Readonly<ContextVariablesSectionProps>) {
  const entries = Object.entries(registration.contextVariables ?? {});
  if (entries.length === 0) {
    return (
      <Text size="sm" color="muted">
        No context variables declared for this component.
      </Text>
    );
  }
  const captionText =
    capture !== undefined
      ? buildCaptureCaption(capture)
      : 'No live capture yet — open a page that renders this component to populate the values below.';
  return (
    <VStack gap={2}>
      <Text size="sm" color="muted">
        {captionText}
      </Text>
      <VStack gap={1}>
        {entries.map(([name, descriptor]) => {
          const liveValue = capture !== undefined ? capture.context[name] : undefined;
          return (
            <LiveContextRow
              key={name}
              name={name}
              descriptor={descriptor}
              liveValue={liveValue}
              hasCapture={capture !== undefined}
              onInsert={onInsert}
            />
          );
        })}
      </VStack>
    </VStack>
  );
}

function buildCaptureCaption(capture: RuntimeContextCapture): string {
  let when: string;
  try {
    when = new Date(capture.capturedAt).toLocaleString();
  } catch {
    when = capture.capturedAt;
  }
  if (capture.claimReference !== undefined && capture.claimReference !== '') {
    return `Last captured ${when} from claim ${capture.claimReference}.`;
  }
  return `Last captured ${when}.`;
}

// ---------------------------------------------------------------------------
// Sub-section: Existing rules table
// ---------------------------------------------------------------------------

interface ExistingRulesSectionProps {
  readonly rulesQuery: ReturnType<typeof useComponentRulesList>;
  readonly rules: ComponentRuleDTO[];
  readonly onActivate: (rule: ComponentRuleDTO) => void;
  readonly onArchive: (rule: ComponentRuleDTO) => void;
  readonly onEditJson: (rule: ComponentRuleDTO) => void;
  readonly onEditNl: (rule: ComponentRuleDTO) => void;
  readonly onRequestDelete: (rule: ComponentRuleDTO) => void;
}

/**
 * Renders the rules table with status-aware row actions. Extracted so
 * the parent page component stays legible; the sub-component has no
 * internal state of its own.
 */
function ExistingRulesSection({
  rulesQuery,
  rules,
  onActivate,
  onArchive,
  onEditJson,
  onEditNl,
  onRequestDelete,
}: Readonly<ExistingRulesSectionProps>) {
  if (rulesQuery.isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-8">
        <Spinner size="md" />
        <Text size="sm" color="muted">
          Loading rules...
        </Text>
      </VStack>
    );
  }

  if (rulesQuery.isError) {
    return (
      <Alert variant="error" title="Failed to load rules">
        {rulesQuery.error instanceof Error
          ? rulesQuery.error.message
          : 'An unexpected error occurred while loading rules.'}
      </Alert>
    );
  }

  if (rules.length === 0) {
    return (
      <Alert variant="info" title="No rules defined yet">
        Use the form below to create one.
      </Alert>
    );
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Description</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Priority</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {rules.map((rule) => (
          <Table.Row key={rule.id}>
            <Table.Cell>
              <Text weight="medium">{rule.name}</Text>
            </Table.Cell>
            <Table.Cell>
              <Text size="sm" color="muted">
                {rule.description ?? '--'}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <HStack gap={1} align="center">
                <Badge variant={statusBadgeVariant(rule.status)} size="sm">
                  {rule.status}
                </Badge>
                <Badge variant={rule.visibility === 'public' ? 'info' : 'default'} size="sm">
                  {rule.visibility === 'public' ? 'Public' : 'Private'}
                </Badge>
              </HStack>
            </Table.Cell>
            <Table.Cell>
              <Text size="sm">{rule.priority}</Text>
            </Table.Cell>
            <Table.Cell align="right">
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Actions for ${rule.name}`}
                    leftIcon={<Icon icon={MoreHorizontal} size="sm" />}
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {rule.status === 'draft' && (
                    <DropdownMenu.Item
                      icon={<Icon icon={CheckCircle2} size="sm" />}
                      onSelect={() => {
                        onActivate(rule);
                      }}
                    >
                      Activate
                    </DropdownMenu.Item>
                  )}
                  {rule.status === 'active' && (
                    <DropdownMenu.Item
                      onSelect={() => {
                        onArchive(rule);
                      }}
                    >
                      Archive
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item
                    icon={<Icon icon={Code2} size="sm" />}
                    onSelect={() => {
                      onEditJson(rule);
                    }}
                  >
                    View / edit JSON
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    icon={<Icon icon={Pencil} size="sm" />}
                    onSelect={() => {
                      onEditNl(rule);
                    }}
                  >
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    destructive
                    icon={<Icon icon={Trash2} size="sm" />}
                    onSelect={() => {
                      onRequestDelete(rule);
                    }}
                  >
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Translate preview modal
// ---------------------------------------------------------------------------

interface TranslatePreviewModalProps {
  readonly pendingTranslation: TranslateComponentRuleResponse | null;
  /**
   * The component the rule targets. Used to run the registration-aware
   * effect guardrail so an LLM-translated `filterPropValues` against a
   * non-filterable prop is caught (and blocked) before the admin saves it.
   */
  readonly registration: ComponentRegistration<unknown>;
  readonly isSaving: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

/**
 * Renders the preview modal for a translated rule. Shown when
 * `pendingTranslation !== null`. The admin can either cancel (which
 * clears the state without saving) or confirm (which fires the parent
 * page's `onSave` handler, responsible for calling `createRule`).
 *
 * Before allowing the save, the translated effects are run through
 * {@link validateEffectsAgainstRegistration}: any `filterPropValues` effect
 * targeting a prop the component does not expose as a filterable data array is
 * surfaced as a blocking error (with a `setProp` suggestion), so the silent
 * runtime no-op never reaches the backend.
 */
function TranslatePreviewModal({
  pendingTranslation,
  registration,
  isSaving,
  onCancel,
  onSave,
}: Readonly<TranslatePreviewModalProps>) {
  const isOpen = pendingTranslation !== null;
  const confidencePct = useMemo(() => {
    if (pendingTranslation === null) return 0;
    return Math.round(pendingTranslation.confidence * 100);
  }, [pendingTranslation]);

  const effectGuardErrors = useMemo(() => {
    if (pendingTranslation === null) return [];
    return validateEffectsAgainstRegistration(
      pendingTranslation.rule.effects,
      registration,
    );
  }, [pendingTranslation, registration]);
  const hasEffectGuardErrors = effectGuardErrors.length > 0;

  return (
    <Modal open={isOpen} onClose={onCancel} size="lg">
      <Modal.Header title="Preview translated rule" />
      <Modal.Body>
        {pendingTranslation !== null && (
          <VStack gap={4}>
            <HStack gap={3} align="center">
              <Badge variant={confidencePct >= 80 ? 'success' : 'warning'} size="md">
                {`Confidence: ${String(confidencePct)}%`}
              </Badge>
              <Text size="sm" color="muted">
                {pendingTranslation.reasoning}
              </Text>
            </HStack>

            <VStack gap={2}>
              <Text as="h3" size="base" weight="semibold">
                Parsed conditions
              </Text>
              <Card variant="outlined" padding="sm">
                <Card.Content>
                  <pre className="whitespace-pre-wrap font-mono text-xs">
                    {JSON.stringify(pendingTranslation.rule.conditions, null, 2)}
                  </pre>
                </Card.Content>
              </Card>
            </VStack>

            <VStack gap={2}>
              <Text as="h3" size="base" weight="semibold">
                Parsed effects
              </Text>
              {pendingTranslation.rule.effects.length === 0 ? (
                <Alert variant="warning" title="No effects parsed">
                  The translator did not produce any effects for this rule. Saving it would do
                  nothing at runtime.
                </Alert>
              ) : (
                <HStack gap={2} wrap>
                  {pendingTranslation.rule.effects.map((effect, index) => (
                    <Badge key={`${effect.type}-${String(index)}`} variant="primary" size="sm">
                      {describeEffect(effect)}
                    </Badge>
                  ))}
                </HStack>
              )}
            </VStack>

            {hasEffectGuardErrors && (
              <Alert variant="error" title="Effect not allowed">
                <span className="whitespace-pre-line">
                  {effectGuardErrors.map((e) => e.message).join('\n')}
                </span>
              </Alert>
            )}
          </VStack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSaving}
            onClick={onSave}
            disabled={pendingTranslation === null || hasEffectGuardErrors}
          >
            Save as draft
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Edit rule JSON modal
// ---------------------------------------------------------------------------

interface EditRuleJsonModalProps {
  readonly rule: ComponentRuleDTO | null;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * Visibility currently selected in the modal. `null` when the modal is
   * closed (no rule to seed from). Non-admin users see the selector
   * disabled; the radio state is still driven by this prop so the
   * current scope remains visible.
   */
  readonly visibility: 'public' | 'private' | null;
  readonly onVisibilityChange: (value: 'public' | 'private') => void;
  readonly isAdminTenant: boolean;
  /**
   * Resolves a tenant id to its display name. Used for the read-only
   * "Scoped to tenant" caption. Falls back to the raw id (returns
   * `undefined`) when the tenant has been deleted.
   */
  readonly tenantNameById: (id: string) => string | undefined;
  readonly error: string | null;
  readonly isSaving: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly saveDisabled: boolean;
}

/**
 * Modal for viewing and editing the raw `rule_json` of an existing rule.
 * Open state is driven by `rule` being non-null; closing is the parent's
 * responsibility (call `onCancel`). The textarea is controlled from the
 * outside so the parent can compare the current value against the
 * original to decide whether Save should be enabled.
 */
function EditRuleJsonModal({
  rule,
  value,
  onChange,
  visibility,
  onVisibilityChange,
  isAdminTenant,
  tenantNameById,
  error,
  isSaving,
  onCancel,
  onSave,
  saveDisabled,
}: Readonly<EditRuleJsonModalProps>) {
  const isOpen = rule !== null;
  // Non-admin tenants see the current scope but cannot change it —
  // disable BOTH radios and wrap the group in an explanatory tooltip.
  const disableWholeGroup = !isAdminTenant;
  const visibilityGroup =
    visibility !== null ? (
      <RadioGroup
        name="edit-rule-json-visibility"
        label="Visibility"
        orientation="horizontal"
        value={visibility}
        onChange={(next) => {
          if (next === 'public' || next === 'private') {
            onVisibilityChange(next);
          }
        }}
        options={[
          {
            value: 'private',
            label: 'Private',
            disabled: disableWholeGroup,
          },
          {
            value: 'public',
            label: 'Public',
            disabled: disableWholeGroup,
          },
        ]}
      />
    ) : null;
  return (
    <Modal open={isOpen} onClose={onCancel} size="lg">
      <Modal.Header title="Rule JSON">
        {rule !== null && (
          <HStack gap={2} align="center" className="mt-1">
            <Text size="sm" color="muted">
              {rule.name}
            </Text>
            <Badge variant={statusBadgeVariant(rule.status)} size="sm">
              {rule.status}
            </Badge>
          </HStack>
        )}
      </Modal.Header>
      <Modal.Body>
        <VStack gap={3}>
          {error !== null && (
            <Alert variant="error" title="Invalid rule JSON">
              {error}
            </Alert>
          )}
          {visibilityGroup !== null && (
            <VStack gap={1}>
              {disableWholeGroup ? (
                <Tooltip content="Only admin tenants can change visibility.">
                  {visibilityGroup}
                </Tooltip>
              ) : (
                visibilityGroup
              )}
              {rule !== null && rule.tenant_id !== null && (
                <Text size="sm" color="muted">
                  Scoped to tenant: {tenantNameById(rule.tenant_id) ?? rule.tenant_id}
                </Text>
              )}
            </VStack>
          )}
          <textarea
            aria-label="Rule JSON editor"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            rows={16}
            className="w-full font-mono text-xs border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-primary-400"
          />
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSaving}
            onClick={onSave}
            disabled={saveDisabled || isSaving}
          >
            Save
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Visibility radio group
// ---------------------------------------------------------------------------

interface VisibilityRadioGroupProps {
  readonly value: 'public' | 'private';
  readonly onChange: (value: 'public' | 'private') => void;
  readonly isAdminTenant: boolean;
}

/**
 * Public / Private selector for the add-rule form.
 *
 * The Public option is disabled for non-admin tenants; we wrap the whole
 * group in a `Tooltip` in that case so hovering anywhere over the
 * control reveals the reason. RadioGroup takes options as a prop so we
 * can't attach the tooltip to the Public radio alone — the group-level
 * tooltip is the most honest signal available through the existing API.
 *
 * The caption below is rendered unconditionally so admins and non-admins
 * both see the contract (Public = shared across tenants, requires admin).
 */
function VisibilityRadioGroup({
  value,
  onChange,
  isAdminTenant,
}: Readonly<VisibilityRadioGroupProps>) {
  const publicDisabled = !isAdminTenant;
  const group = (
    <RadioGroup
      name="component-rule-visibility"
      label="Visibility"
      orientation="horizontal"
      value={value}
      onChange={(next) => {
        // The RadioGroup callback is `(value: string) => void`; narrow
        // back to the union before handing it to the parent.
        if (next === 'public' || next === 'private') {
          onChange(next);
        }
      }}
      options={[
        { value: 'private', label: 'Private' },
        { value: 'public', label: 'Public', disabled: publicDisabled },
      ]}
    />
  );
  return (
    <VStack gap={1} className="mt-2">
      {publicDisabled ? (
        <Tooltip content="Only admin tenants can create public rules.">{group}</Tooltip>
      ) : (
        group
      )}
      <Text size="sm" color="muted">
        Public rules are shared across all tenants. Requires admin.
      </Text>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Target tenant selector
// ---------------------------------------------------------------------------

interface TargetTenantSelectProps {
  readonly value: string | null;
  readonly onChange: (id: string) => void;
  readonly options: ReadonlyArray<{ value: string; label: string }>;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Searchable tenant picker for a Private rule. Shared by both rule-authoring
 * surfaces (the NL "Add a rule" form and the WYSIWYG "Edit layout" card) so
 * the Select + loading/error-Alert behaviour stays in one place.
 *
 * Callers gate rendering on `isAdminTenant && visibility === 'private'`; this
 * component assumes it is only mounted when a tenant choice is meaningful.
 */
function TargetTenantSelect({
  value,
  onChange,
  options,
  isLoading,
  isError,
}: Readonly<TargetTenantSelectProps>) {
  return (
    <VStack gap={1} className="mt-2">
      <Select
        label="Target tenant"
        required
        searchable
        options={options as { value: string; label: string }[]}
        value={value ?? ''}
        onChange={(v) => {
          if (typeof v === 'string') onChange(v);
        }}
        disabled={isLoading}
        placeholder={isLoading ? 'Loading tenants...' : 'Select a tenant'}
        error={isError}
        errorMessage={isError ? 'Failed to load tenants.' : undefined}
      />
      {isError ? (
        <Alert variant="warning" title="Tenant list unavailable">
          Tenant list unavailable — private rules will be scoped to your own tenant.
        </Alert>
      ) : (
        <Text size="sm" color="muted">
          Private rules are scoped to the chosen tenant only.
        </Text>
      )}
    </VStack>
  );
}

/**
 * Render a `RuleEffect` as a short human-readable label for a `Badge`.
 * Kept as a pure helper so the modal body doesn't carry rendering logic.
 */
function describeEffect(effect: RuleEffect): string {
  switch (effect.type) {
    case 'hide':
      return 'hide';
    case 'show':
      return 'show';
    case 'disable':
      return 'disable';
    case 'requireProp':
      return `requireProp: ${effect.prop}`;
    case 'setProp':
      return `setProp: ${effect.prop} = ${JSON.stringify(effect.value)}`;
    case 'filterPropValues':
      return `filterPropValues: ${effect.prop} ∈ [${effect.allowed.join(', ')}]`;
    case 'renameField':
      return `renameField: ${effect.key} → "${effect.label}"`;
    case 'hideField':
      return `hideField: ${effect.key}`;
    case 'reorderFields':
      return `reorderFields: [${effect.order.join(', ')}]`;
    case 'renameColumn':
      return `renameColumn: ${effect.key} → "${effect.label}"`;
    case 'hideColumn':
      return `hideColumn: ${effect.key}`;
    case 'reorderColumns':
      return `reorderColumns: [${effect.order.join(', ')}]`;
    case 'reorderSections':
      return `reorderSections: [${effect.order.join(', ')}]`;
    case 'setFieldTooltip':
      return `setFieldTooltip: ${effect.key} → "${effect.tooltip}"`;
    case 'setColumnTooltip':
      return `setColumnTooltip: ${effect.key} (${effect.target ?? 'header'}) → "${effect.tooltip}"`;
    case 'setFieldPlaceholder':
      return `setFieldPlaceholder: ${effect.key} → "${effect.placeholder}"`;
    case 'setFieldHelperText':
      return `setFieldHelperText: ${effect.key} → "${effect.helperText}"`;
    case 'setTitle':
      return `setTitle: "${effect.title}"`;
    case 'setActionLabel':
      return `setActionLabel: ${effect.action} → "${effect.label}"`;
    case 'hideAction':
      return `hideAction: ${effect.action}`;
    case 'clearFieldTooltip':
      return `clearFieldTooltip: ${effect.key}`;
    case 'clearColumnTooltip':
      return `clearColumnTooltip: ${effect.key} (${effect.target ?? 'header'})`;
    case 'showField':
      return `showField: ${effect.key}`;
    case 'showColumn':
      return `showColumn: ${effect.key}`;
    case 'setDefaultSort':
      return `setDefaultSort: ${effect.column} ${effect.direction}`;
    case 'setFieldCondition': {
      // `key ← when <source> <operator> <value?> → visible=…, disabled=…`.
      // The value is omitted for isEmpty / isNotEmpty, and only the keys the
      // effect actually sets are listed.
      const predicate =
        effect.value === undefined
          ? `when ${effect.when} ${effect.operator}`
          : `when ${effect.when} ${effect.operator} ${JSON.stringify(effect.value)}`;
      const applied = (['visible', 'disabled', 'required', 'checked'] as const)
        .filter((prop) => effect.effect[prop] !== undefined)
        .map((prop) => `${prop}=${String(effect.effect[prop])}`)
        .join(', ');
      return `setFieldCondition: ${effect.key} ← ${predicate} → ${applied}`;
    }
    default:
      return 'unknown effect';
  }
}
